import { Link } from "react-router-dom";
import {
  ArrowRight,
  HelpCircle,
  Inbox,
  Layers3,
  PanelTop,
  PhoneCall,
  ShieldCheck,
  type LucideIcon,
} from "lucide-react";

import { cn } from "@/app/components/ui/utils";

/**
 * Homepage 重头戏：Console Mode 高保真原型入口。
 *
 * 视觉语言对齐首页其他章节：粗野黑边 + 毛玻璃半透明色块（bg-color/75 + backdrop-blur-sm），
 * 角色色用品牌 token（caller=teal, general=blue）。
 */

type ConsoleTile = {
  href: string;
  title: string;
  kicker: "GENERAL" | "CALLER";
  /** Brand role accent — drives both the icon chip and the glass tile tint. */
  accent: "blue" | "teal";
  icon: LucideIcon;
  blurb: string;
};

/** Solid color for the inset icon chip (matches brutalist tone). */
const ROLE_CHIP_BG: Record<ConsoleTile["accent"], string> = {
  blue: "bg-[#3B82F6]",
  teal: "bg-[#14B8A6]",
};

/** Glass tile body — translucent role color over the cream paper. */
const ROLE_TILE_BG: Record<ConsoleTile["accent"], string> = {
  blue: "bg-[#3B82F6]/15 hover:bg-[#3B82F6]/25",
  teal: "bg-[#14B8A6]/15 hover:bg-[#14B8A6]/25",
};

const TILES: ConsoleTile[] = [
  {
    href: "/console/dashboard",
    title: "工作台",
    kicker: "GENERAL",
    accent: "blue",
    icon: PanelTop,
    blurb: "4 进程健康灯 + 真实 4 步上手清单",
  },
  {
    href: "/console/catalog",
    title: "热线目录",
    kicker: "CALLER",
    accent: "teal",
    icon: Layers3,
    blurb: "唯一发起调用入口 · TryCallDrawer 自动表单",
  },
  {
    href: "/console/calls",
    title: "调用记录",
    kicker: "CALLER",
    accent: "teal",
    icon: PhoneCall,
    blurb: "人话通话日志 + 摘要 / 背景 / 结果三段式",
  },
  {
    href: "/console/approvals",
    title: "审批中心",
    kicker: "CALLER",
    accent: "teal",
    icon: Inbox,
    blurb: "5 filter tab + 审批疲劳教育 banner",
  },
  {
    href: "/console/access-lists",
    title: "名单管理",
    kicker: "CALLER",
    accent: "teal",
    icon: ShieldCheck,
    blurb: "Responder / Hotline 白 + 黑名单 + 三模式",
  },
  {
    href: "/console/help",
    title: "上手 & 帮助",
    kicker: "GENERAL",
    accent: "blue",
    icon: HelpCircle,
    blurb: "sticky TOC + 8 篇 5 分钟跑通文章",
  },
];

export function HomepageConsoleShowcase() {
  return (
    <section
      id="console-prototype"
      className="px-4 sm:px-6 lg:px-8 pb-16 lg:pb-24"
    >
      <div className="mx-auto max-w-7xl">
        {/* Outer brutalist frame — glass white surface so the page印花 shows through. */}
        <div className="border-[6px] border-black bg-white/80 backdrop-blur-md shadow-[14px_14px_0px_0px_rgba(0,0,0,1)] relative">
          {/* Top stripe label — solid black for legibility */}
          <div className="bg-black text-white px-6 py-3 flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <span className="bg-[#A3E635] text-black border-2 border-[#A3E635] px-2 py-0.5 text-[11px] font-black tracking-widest uppercase">
                LIVE PROTOTYPE
              </span>
              <span className="text-[11px] font-black tracking-widest uppercase text-white/80">
                Delegated-Execution · Ops Console
              </span>
            </div>
            <Link
              to="/console"
              className="text-[11px] font-black tracking-widest uppercase text-[#A3E635] hover:text-white inline-flex items-center gap-1.5"
            >
              打开原型 hub
              <ArrowRight className="h-3 w-3" strokeWidth={3} />
            </Link>
          </div>

          <div className="p-6 sm:p-10 lg:p-12">
            {/* Header row — text left, primary CTA right */}
            <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-10">
              <div className="max-w-3xl">
                <p className="text-xs font-black uppercase tracking-widest text-zinc-500 mb-3">
                  LIVE PROTOTYPE · 6 个真实页面 · 静态数据
                </p>
                <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black uppercase leading-[0.95] tracking-tight text-black">
                  开通一根 Hotline 之后，
                  <br />
                  管理它的{" "}
                  <span className="bg-[#A3E635] px-3 inline-block">界面</span>
                  <br />
                  是什么样？
                </h2>
                <div className="mt-6 border-4 border-black bg-white/85 backdrop-blur-sm p-5 shadow-[6px_6px_0px_0px_rgba(0,0,0,1)] max-w-2xl">
                  <p className="text-base sm:text-lg font-bold text-zinc-800 leading-relaxed">
                    不是 mockup，不是 PDF —— 而是部署在每个超级个体本地的一套高保真 Console 原型，
                    <span className="bg-[#FACC15] px-1.5 py-0.5 text-black ml-0.5">
                      6 个页面均可交互
                    </span>
                    。
                  </p>
                </div>
              </div>
              <Link
                to="/console/dashboard"
                className="inline-flex items-center justify-center gap-2 border-4 border-black bg-[#A3E635]/85 backdrop-blur-sm px-8 py-4 text-sm font-black uppercase text-black shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] transition-transform hover:-translate-x-1 hover:-translate-y-1 hover:shadow-[12px_12px_0px_0px_rgba(0,0,0,1)] shrink-0"
              >
                直接进工作台
                <ArrowRight className="h-5 w-5" strokeWidth={3} />
              </Link>
            </div>

            {/* 6-tile grid — each tile is a glass colored card */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {TILES.map((t) => {
                const Icon = t.icon;
                return (
                  <Link
                    key={t.href}
                    to={t.href}
                    className={cn(
                      "group block border-4 border-black backdrop-blur-sm p-5 shadow-[6px_6px_0px_0px_rgba(0,0,0,1)]",
                      "transition-all hover:-translate-x-1 hover:-translate-y-1 hover:shadow-[10px_10px_0px_0px_rgba(0,0,0,1)]",
                      ROLE_TILE_BG[t.accent],
                    )}
                  >
                    <div className="flex items-center justify-between mb-4">
                      <span
                        className={cn(
                          "inline-flex items-center justify-center h-10 w-10 border-2 border-black text-white shadow-[3px_3px_0px_0px_rgba(0,0,0,1)]",
                          ROLE_CHIP_BG[t.accent],
                        )}
                      >
                        <Icon className="h-5 w-5" strokeWidth={2.5} />
                      </span>
                      <span className="text-[10px] font-black tracking-widest text-zinc-700">
                        {t.kicker}
                      </span>
                    </div>
                    <p className="text-lg font-black text-black uppercase tracking-tight">
                      {t.title}
                    </p>
                    <p className="text-[12px] font-bold text-zinc-700 mt-1.5 leading-snug">
                      {t.blurb}
                    </p>
                    <p className="text-[11px] font-black tracking-widest text-black mt-4 inline-flex items-center gap-1 uppercase">
                      打开原型
                      <ArrowRight
                        className="h-3 w-3 transition-transform group-hover:translate-x-0.5"
                        strokeWidth={3}
                      />
                    </p>
                  </Link>
                );
              })}
            </div>

            {/* Bottom strip: positioning relative to other entries */}
            <div className="mt-10 pt-6 border-t-4 border-black/15 grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="border-2 border-black/30 bg-white/60 backdrop-blur-sm p-4">
                <p className="text-[10.5px] font-black tracking-widest text-zinc-500 mb-1.5 uppercase">
                  这是给谁看的
                </p>
                <p className="text-sm font-bold text-zinc-700 leading-relaxed">
                  Agent 应用的产品 / 工程 / 设计同事 —— 想看「Delegated Execution
                  跑起来到底长啥样」的人。
                </p>
              </div>
              <div className="border-2 border-black/30 bg-white/60 backdrop-blur-sm p-4">
                <p className="text-[10.5px] font-black tracking-widest text-zinc-500 mb-1.5 uppercase">
                  和 Marketplace 的关系
                </p>
                <p className="text-sm font-bold text-zinc-700 leading-relaxed">
                  <Link to="/marketplace" className="underline underline-offset-2 hover:text-black">
                    /marketplace
                  </Link>{" "}
                  展示「公开的 Hotline 目录」；这里展示「装在你本地的 Console」。
                </p>
              </div>
              <div className="border-2 border-black/30 bg-white/60 backdrop-blur-sm p-4">
                <p className="text-[10.5px] font-black tracking-widest text-zinc-500 mb-1.5 uppercase">
                  和 Playground 的关系
                </p>
                <p className="text-sm font-bold text-zinc-700 leading-relaxed">
                  <Link to="/playground" className="underline underline-offset-2 hover:text-black">
                    /playground
                  </Link>{" "}
                  是设计系统总览；这里是 Console mode 子方言的展开应用。
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
